import WorkspaceCanvas from '@/components/WorkspaceCanvas';
import { useT } from '@/i18n';
import { useBottomBarInset } from '@/utils/useBottomBarInset';
import { useIsWideViewport } from '@/utils/useMediaQuery';
import { useNavOffset } from '@/utils/useNavOffset';
import type { TableState } from '@restaurant/api-client';
import {
  Link,
  List,
  ListItem,
  NavLeft,
  NavRight,
  NavTitle,
  Navbar,
  Page,
  Popover,
  Subnavbar,
  f7,
} from 'framework7-react';
import { type MutableRefObject, useEffect, useMemo, useRef, useState } from 'react';
import TableSessionInspector from '../components/TableSessionInspector';
import ZonePicker, { type ZoneTab } from '../components/ZonePicker';
import { useExpectedArrivals, useOpenSession, useTableStates } from '../hooks/useTableOrdering';
import { useWaitlist } from '@/features/waitlist/hooks/useWaitlist';
import FloorPlanLiveView from '../views/FloorPlanLiveView';
import TableGridView from '../views/TableGridView';

type ViewMode = 'FLOOR' | 'LIST';

// Matches the F7-react Popover ref shape (mirrors ZonePicker).
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type PopoverInstance = { el: HTMLElement | null; f7Popover: () => any };

/**
 * Tables workspace - the dine-in command surface. Same full-height shell as
 * the reservations workspace: a non-scrolling page whose split flex-fills the
 * viewport, so the Floor canvas gets real height to pan/zoom in and the
 * inspector docks and scrolls independently. "Floor" draws the actual saved
 * plan on the shared interactive canvas; "List" is the dense scannable view.
 */
export default function TablesWorkspacePage() {
  const tAll = useT();
  const t = tAll.tableOrdering.workspace;
  const VIEW_META: Record<ViewMode, { label: string; icon: string }> = {
    FLOOR: { label: t.floor, icon: 'map' },
    LIST: { label: t.list, icon: 'list_bullet' },
  };
  const isWide = useIsWideViewport();
  const [mode, setMode] = useState<ViewMode>('FLOOR');
  const viewPopRef = useRef<PopoverInstance>(null);
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null);
  const [selectedTableId, setSelectedTableId] = useState<string | null>(null);
  const [pickedZone, setPickedZone] = useState<string | null | undefined>(undefined);
  const openSession = useOpenSession();
  const { data: states } = useTableStates();
  const { arrivals } = useExpectedArrivals();
  const { data: waitlist } = useWaitlist();
  const waitingCount = waitlist?.length ?? 0;

  // Zone list with each zone's LIVE situation (first-seen order, "No zone"
  // last) so staff see what's happening in zones they're NOT looking at -
  // open-tab count + an "needs attention" count (service calls + bill
  // requests). This is what makes scoping-by-zone safe.
  const zones = useMemo<ZoneTab[]>(() => {
    const order: (string | null)[] = [];
    const acc = new Map<string | null, { label: string; open: number; attention: number }>();
    for (const state of states ?? []) {
      const key = state.zoneId;
      if (!acc.has(key)) {
        acc.set(key, {
          label: key === null ? t.noZone : (state.zoneLabel ?? t.zoneFallback),
          open: 0,
          attention: 0,
        });
        order.push(key);
      }
      const z = acc.get(key)!;
      if (state.state === 'OCCUPIED') z.open += 1;
      z.attention +=
        state.pendingServiceRequests + (state.sessionStatus === 'BILL_REQUESTED' ? 1 : 0);
    }
    // "No zone" always last.
    order.sort((a, b) => (a === null ? 1 : 0) - (b === null ? 1 : 0));
    return order.map((id) => {
      const z = acc.get(id)!;
      return { id, label: z.label, open: z.open, attention: z.attention };
    });
  }, [states, t.noZone, t.zoneFallback]);

  // Resolved selection: the user's pick if still valid, else the first zone.
  const activeZoneId =
    pickedZone !== undefined && zones.some((z) => z.id === pickedZone)
      ? pickedZone
      : (zones[0]?.id ?? null);

  useEffect(() => {
    if (!isWide && selectedSessionId) {
      setSelectedSessionId(null);
      setSelectedTableId(null);
    }
  }, [isWide, selectedSessionId]);

  // Full-height shell (`pageContent={false}`): our inner scrollers slide under
  // the blurred bar using the measured `--rsv-nav-offset`. The Subnavbar only
  // exists on Floor (the List drops zone scoping), so the offset changes on
  // every view switch - the shared hook re-measures via a MutationObserver
  // when the bar mounts/unmounts. Same hook as ReservationsWorkspacePage.
  // The px value also feeds the Floor canvas so its fit rests below the bar
  // while content still slides under it when dragged. The bottom tab bar is
  // measured the same way for the opposite edge.
  const navOffset = useNavOffset('tables');
  const tabBarInset = useBottomBarInset();

  function goToSession(sessionId: string, tableId: string) {
    if (isWide) {
      setSelectedSessionId(sessionId);
      setSelectedTableId(tableId);
    } else {
      f7.views.current.router.navigate(`/tables/session/${sessionId}`);
    }
  }

  function openTab(table: TableState) {
    f7.dialog.confirm(t.openTabConfirm(table.label), t.openTabTitle, () => {
      openSession.mutate(
        { tableId: table.tableId },
        {
          onSuccess: (s) => {
            f7.toast.show({ text: t.tabOpened(table.label), closeTimeout: 2000 });
            goToSession(s.id, table.tableId);
          },
        },
      );
    });
  }

  function handleSelect(table: TableState) {
    if (table.sessionId) goToSession(table.sessionId, table.tableId);
    else openTab(table);
  }

  const floor =
    mode === 'FLOOR' ? (
      <FloorPlanLiveView
        activeZoneId={activeZoneId}
        selectedId={selectedTableId}
        onSelect={handleSelect}
        topInset={navOffset}
        bottomInset={tabBarInset}
      />
    ) : (
      // List isn't scoped to a zone - it shows every table grouped under a
      // per-zone heading (omitting `activeZoneId` triggers the grouped view),
      // so the whole floor is scannable in one scroll. Zone scoping only makes
      // sense for the canvas, where off-zone tables are hidden.
      <TableGridView mode="LIST" selectedId={selectedTableId} onSelect={handleSelect} />
    );

  return (
    <Page name="tables" pageContent={false}>
      <Navbar>
        {/* Leading dropdown = the view switcher. Keeps the bar uncluttered
            (the title isn't a lone word and the Subnavbar carries only the
            zone), and uses the same anchored-Popover idiom as the zone
            dropdown for one consistent interaction. */}
        <NavLeft>
          <Link
            className="to-view-trigger"
            onClick={(e) => {
              if (viewPopRef.current?.el) f7.popover.open(viewPopRef.current.el, e.currentTarget);
            }}
          >
            <i className="f7-icons">{VIEW_META[mode].icon}</i>
            <span>{VIEW_META[mode].label}</span>
            <i className="f7-icons to-view-trigger__chev">chevron_down</i>
          </Link>
        </NavLeft>
        <NavTitle>{t.tables}</NavTitle>
        {/* History = the day view of closed tabs, for rechecking a settled
            visit. Action icon lives in NavRight (FRONTEND_PRACTICES Rule 9). */}
        <NavRight>
          {/* Expected arrivals - today's due bookings, seat in one tap. */}
          <Link
            href="/tables/arrivals"
            iconF7="person_2"
            iconBadge={arrivals.length > 0 ? arrivals.length : undefined}
            badgeColor="orange"
          />
          {/* Walk-in waitlist - parties waiting with no free table yet. */}
          <Link
            href="/tables/waitlist"
            iconF7="hourglass"
            iconBadge={waitingCount > 0 ? waitingCount : undefined}
            badgeColor="red"
          />
          <Link href="/tables/history" iconF7="clock" />
        </NavRight>
        {/* Zone scoping only applies to the Floor canvas (it hides off-zone
            tables); the List shows every zone grouped, so the picker - and the
            whole Subnavbar - is dropped there. */}
        {mode === 'FLOOR' && (
          <Subnavbar>
            <div className="to-bar">
              <ZonePicker zones={zones} activeId={activeZoneId} onChange={setPickedZone} />
            </div>
          </Subnavbar>
        )}
      </Navbar>

      <Popover
        ref={viewPopRef as MutableRefObject<PopoverInstance>}
        className="to-view-pop"
        style={{ minWidth: 200 }}
      >
        <List dividers>
          {(['FLOOR', 'LIST'] as ViewMode[]).map((m) => (
            <ListItem
              key={m}
              link="#"
              noChevron
              title={VIEW_META[m].label}
              onClick={() => {
                setMode(m);
                f7.popover.close();
              }}
            >
              <i slot="media" className="f7-icons">
                {VIEW_META[m].icon}
              </i>
              {mode === m && (
                <i
                  slot="after"
                  className="f7-icons"
                  style={{ color: 'var(--f7-theme-color)', fontSize: 20 }}
                >
                  checkmark_alt
                </i>
              )}
            </ListItem>
          ))}
        </List>
      </Popover>

      <WorkspaceCanvas
        isWide={isWide}
        bottomBarInset={tabBarInset}
        main={floor}
        inspector={
          selectedSessionId ? <TableSessionInspector sessionId={selectedSessionId} /> : null
        }
      />
    </Page>
  );
}
